import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Presentation, Download, Send, Loader2, Mail, Clock } from "lucide-react";
import { authHeaders } from "@/hooks/useAdminData";
import { formatDateTimeIT } from "@/utils/datetime";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PAGES = [
  ["Copertina", "Foto, claim e riquadro \"Preparata per [azienda]\""],
  ["Chi siamo", "Storia, numeri chiave, valori e come lavoriamo"],
  ["Servizi", "Trasporti, ritiri, magazzino/logistica e servizi extra"],
  ["Zone, portale, contatti", "Aree servite, tracking e area clienti, contatti e CTA"],
];

export default function AdminBrochure() {
  const [form, setForm] = useState({ company: "", contact: "", to_email: "", subject: "", message: "" });
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    axios.get(`${API}/admin/brochure/history`, authHeaders()).then((r) => setHistory(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const download = async () => {
    setDownloading(true);
    try {
      const q = new URLSearchParams({ company: form.company, contact: form.contact }).toString();
      const res = await axios.get(`${API}/admin/brochure/pdf?${q}`, { ...authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `presentazione_MV_Trasporti${form.company ? "_" + form.company.replace(/\s+/g, "_") : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore generazione PDF");
    } finally {
      setDownloading(false);
    }
  };

  const send = async () => {
    if (!form.to_email.includes("@")) return toast.error("Inserisci un indirizzo email valido");
    setSending(true);
    try {
      await axios.post(`${API}/admin/brochure/send`, form, authHeaders());
      toast.success(`Presentazione inviata a ${form.to_email}`);
      setForm((f) => ({ ...f, to_email: "", message: "" }));
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio email");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none bg-white";
  const labelCls = "block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1";

  return (
    <div className="space-y-6" data-testid="admin-brochure-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[#09090B] font-heading">Presentazione Servizi</h1>
        <p className="text-sm text-zinc-500 mt-1">Brochure PDF di 4 pagine con logo e dati MV Trasporti, personalizzabile con il nome del potenziale cliente e inviabile via email.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Azienda destinataria</label>
              <input value={form.company} onChange={set("company")} placeholder="es. Rossi S.p.A." className={inputCls} data-testid="brochure-company" />
            </div>
            <div>
              <label className={labelCls}>Referente (opzionale)</label>
              <input value={form.contact} onChange={set("contact")} placeholder="es. Sig. Mario Rossi" className={inputCls} data-testid="brochure-contact" />
            </div>
          </div>
          <p className="text-xs text-zinc-400 -mt-2">Se compili l'azienda, la copertina riporta "Preparata per …" e la seconda pagina si apre con "Gentile …". Lascia vuoto per una versione generica.</p>

          <div className="flex flex-wrap gap-3">
            <button onClick={download} disabled={downloading} className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-300 text-xs font-bold uppercase tracking-widest hover:border-[#FF5200] hover:text-[#FF5200] disabled:opacity-40 rounded-sm" data-testid="brochure-download-btn">
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Scarica PDF
            </button>
          </div>

          <div className="border-t border-zinc-200 pt-5 space-y-4">
            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Mail size={14} /> Invia via email</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Email destinatario *</label>
                <input type="email" value={form.to_email} onChange={set("to_email")} placeholder="cliente@azienda.it" className={inputCls} data-testid="brochure-to-email" />
              </div>
              <div>
                <label className={labelCls}>Oggetto (opzionale)</label>
                <input value={form.subject} onChange={set("subject")} placeholder="MV Trasporti — Presentazione servizi" className={inputCls} data-testid="brochure-subject" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Messaggio (opzionale)</label>
              <textarea value={form.message} onChange={set("message")} rows={4} placeholder="Testo personalizzato del corpo email. Se vuoto viene usato un messaggio standard." className="w-full px-3 py-2 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none bg-white" data-testid="brochure-message" />
            </div>
            <button onClick={send} disabled={sending || !form.to_email} className="inline-flex items-center gap-2 h-10 px-5 bg-[#FF5200] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#e04a00] disabled:opacity-40 rounded-sm" data-testid="brochure-send-btn">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Invia presentazione
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-[#09090B] text-white rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3"><Presentation size={16} className="text-[#FF5200]" /><span className="text-xs font-bold uppercase tracking-widest">Contenuto</span></div>
            <ol className="space-y-2.5">
              {PAGES.map(([t, d], i) => (
                <li key={t} className="flex gap-3 text-sm">
                  <span className="text-[#FF5200] font-bold font-mono">{i + 1}</span>
                  <div><p className="font-bold">{t}</p><p className="text-xs text-zinc-400">{d}</p></div>
                </li>
              ))}
            </ol>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="brochure-history">
            <div className="flex items-center gap-2 mb-3"><Clock size={14} className="text-zinc-400" /><span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Ultimi invii</span></div>
            {history.length === 0 ? <p className="text-xs text-zinc-400">Nessun invio effettuato.</p> : (
              <ul className="divide-y divide-zinc-100">
                {history.slice(0, 8).map((h) => (
                  <li key={h.id} className="py-2 text-xs">
                    <p className="font-bold text-[#09090B] truncate">{h.company || h.contact || h.to_email}</p>
                    <p className="text-zinc-500 truncate">{h.to_email} · {formatDateTimeIT(h.sent_at)}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
